import React from "react";
import PropTypes from "prop-types";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Cell,
} from "recharts";
import styles from "./TrendingBarChart.module.css";

const BAR_COLORS = ["#91B6BB", "#7F6A45", "#415364", "#ADB7C0", "#9E703C"];
const TIMEFRAME_OPTIONS = ["This Week", "This Month", "This Year"];

function formatYAxisTick(value) {
  if (!Number.isFinite(value)) {
    return "0";
  }

  if (value >= 1000) {
    return `${Math.round(value / 1000)}k`;
  }

  return `${value}`;
}

function toWrappedTickLines(label, maxCharsPerLine = 9, maxLines = 2) {
  const words = String(label || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!words.length) {
    return [""];
  }

  const lines = [];
  let currentLine = "";

  words.forEach((word) => {
    if (!currentLine) {
      currentLine = word;
      return;
    }

    const nextLine = `${currentLine} ${word}`;
    if (nextLine.length <= maxCharsPerLine) {
      currentLine = nextLine;
      return;
    }

    lines.push(currentLine);
    currentLine = word;
  });

  if (currentLine) {
    lines.push(currentLine);
  }

  const limited = lines.slice(0, maxLines);
  if (lines.length > maxLines) {
    const lastIndex = limited.length - 1;
    const clipped = limited[lastIndex].slice(
      0,
      Math.max(0, maxCharsPerLine - 1),
    );
    limited[lastIndex] = `${clipped}...`;
  }

  return limited;
}

export function TrendingBarChart({
  title,
  data,
  timeframe,
  summaryValue,
  onTimeframeChange,
}) {
  const chartData = React.useMemo(() => {
    return [...data]
      .sort((a, b) => Number(b.value || 0) - Number(a.value || 0))
      .slice(0, 5)
      .map((entry) => ({
        label: entry.label,
        value: Number(entry.value) || 0,
      }));
  }, [data]);

  const maxValue = React.useMemo(() => {
    const maxDataValue = Math.max(...chartData.map((item) => item.value), 0);
    const roundedToTenThousand = Math.ceil(maxDataValue / 10000) * 10000;

    return Math.max(50000, roundedToTenThousand);
  }, [chartData]);

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <h3 className={styles.title}>{title}</h3>

        <div className={styles.headerRight}>
          <label className={styles.timeframeLabel}>
            <span className={styles.srOnly}>Select timeframe</span>
            <select
              className={styles.timeframeSelect}
              value={timeframe}
              onChange={(event) => onTimeframeChange(event.target.value)}
              aria-label="Select timeframe"
            >
              {TIMEFRAME_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            <span className={styles.chevron} aria-hidden="true" />
          </label>
          <span className={styles.summaryValue}>{summaryValue}</span>
        </div>
      </div>

      <div className={styles.chartWrap}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            margin={{ top: 8, right: 12, left: 4, bottom: 10 }}
            barCategoryGap="28%"
          >
            <CartesianGrid
              stroke="#E5E5E5"
              strokeDasharray="5 5"
              vertical={false}
            />

            <YAxis
              axisLine={false}
              tickLine={false}
              tickCount={6}
              width={38}
              domain={[0, maxValue]}
              tickFormatter={formatYAxisTick}
              tick={{ fill: "#77838F", fontSize: 12 }}
            />

            <XAxis
              dataKey="label"
              axisLine={false}
              tickLine={false}
              interval={0}
              height={48}
              tickMargin={8}
              tick={({ x, y, payload }) => {
                const lines = toWrappedTickLines(payload.value);

                return (
                  <text
                    x={x}
                    y={y}
                    textAnchor="middle"
                    fill="#77838F"
                    fontSize={12}
                    fontFamily="Open Sans, sans-serif"
                  >
                    {lines.map((line, index) => (
                      <tspan
                        key={`${line}-${index}`}
                        x={x}
                        dy={index === 0 ? 0 : 14}
                      >
                        {line}
                      </tspan>
                    ))}
                  </text>
                );
              }}
            />

            <Bar dataKey="value" radius={[0, 0, 0, 0]} maxBarSize={20}>
              {chartData.map((entry, index) => (
                <Cell
                  key={`${entry.label}-${index}`}
                  fill={BAR_COLORS[index % BAR_COLORS.length]}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

TrendingBarChart.propTypes = {
  title: PropTypes.string.isRequired,
  data: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired,
    }),
  ).isRequired,
  timeframe: PropTypes.oneOf(TIMEFRAME_OPTIONS).isRequired,
  summaryValue: PropTypes.string,
  onTimeframeChange: PropTypes.func.isRequired,
};

TrendingBarChart.defaultProps = {
  summaryValue: "$9,600.84",
};
